import type { HttpActionDef } from "@gencow/core";

const VALID_METHODS = new Set(["GET", "POST", "PUT", "DELETE", "PATCH"]);

function asHttpActionDef(value: unknown): HttpActionDef | undefined {
  if (!value || typeof value !== "object") return undefined;
  const d = value as Partial<HttpActionDef> & { allowAnonymous?: boolean };
  if (typeof d.method !== "string" || !VALID_METHODS.has(d.method)) return undefined;
  if (typeof d.path !== "string" || d.path.length === 0) return undefined;
  if (typeof d.handler !== "function") return undefined;
  return {
    method: d.method as HttpActionDef["method"],
    path: d.path,
    isPublic: d.allowAnonymous === true || d.isPublic === true,
    handler: d.handler as HttpActionDef["handler"],
  };
}

/**
 * Reads `defineApi({ httpRoutes })` from the loaded app module (default export)
 * and returns the route defs in deterministic order (sorted by key) — no global registry.
 *
 * Parallel to `collectProcedureRouteMaps` for procedures. See
 * docs/specs/spec-http-route-api.md.
 */
export function collectHttpRoutesFromApi(appModule: unknown): HttpActionDef[] {
  if (!appModule || typeof appModule !== "object") return [];

  const mod = appModule as { default?: unknown };
  const apiCandidate =
    mod.default !== undefined && mod.default !== null && typeof mod.default === "object"
      ? mod.default
      : appModule;

  const httpRoutes = (apiCandidate as { httpRoutes?: unknown }).httpRoutes;
  if (!httpRoutes || typeof httpRoutes !== "object") return [];

  const entries = Object.entries(httpRoutes as Record<string, unknown>);
  entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));

  const defs: HttpActionDef[] = [];
  for (const [, value] of entries) {
    const def = asHttpActionDef(value);
    if (def) defs.push(def);
  }
  return defs;
}
